import * as React from "react";
import CarouselView, { CarouselViewProps } from "../../views/carousel/carousel-view";
import { Subtract } from "utility-types";

interface ExtraProps {
    readonly onIndexChange: (index: number) => void;
}

interface Handled {
    readonly onStartMove?: (el: HTMLDivElement, touchX: number) => void;
    readonly onMove?: (el: HTMLDivElement, touchX: number, event: Event) => void;
    readonly onEndMove?: (el: HTMLDivElement) => void;
    readonly onClick?: (event: React.MouseEvent) => void;
}

type Props = Subtract<CarouselViewProps, Handled> & ExtraProps;

const MemoizedCarouselView = React.memo(CarouselView);

const Carousel: React.FunctionComponent<Props> = p => {
    const { index, children, onIndexChange } = p;

    const numChildren = React.Children.count(children);

    const dragOffset = React.useRef(0);

    const [startX, setStartX] = React.useState(0);
    const [startOffset, setStartOffset] = React.useState(0);
    const [captureClick, setCaptureClick] = React.useState(false);
    const [scroller, setScroller] = React.useState<HTMLDivElement>();

    const onStartMove = React.useCallback(
        (el: HTMLDivElement, touchX: number) => {
            const offset = index * el.clientWidth;
            setStartX(touchX);

            setStartOffset(offset);
            dragOffset.current = offset;

            setCaptureClick(false);
            setScroller(el);
            el.style.transition = "";
        },
        [index]
    );

    function setScroll(el: HTMLDivElement, left: number, count: number) {
        left = Math.max(0, Math.min((count - 1) * el.clientWidth, left));
        el.style.transform = `translateX(${-left}px)`;
    }

    React.useEffect(() => {
        if (scroller === undefined) return;

        setScroll(scroller, scroller.clientWidth * index, numChildren);
    }, [index, scroller, numChildren]);

    const onMove = React.useCallback(
        (el: HTMLDivElement, touchX: number, event: Event) => {
            const offset = startOffset + (startX - touchX);
            dragOffset.current = offset;

            setScroll(el, offset, numChildren);

            if (!captureClick) {
                setCaptureClick(true);
            }

            const movingRight = startX - touchX > 0;

            if (Math.abs(startX - touchX) > 30) {
                if (numChildren > 1 && event.cancelable) {
                    const cancel =
                        (index > 0 && index < numChildren - 1) ||
                        (index === 0 && movingRight) ||
                        (index === numChildren - 1 && !movingRight);
                    if (cancel) {
                        event.stopPropagation();
                        event.preventDefault();
                    }
                }
            }
        },
        [startX, captureClick, numChildren, startOffset, index]
    );

    const indexRef = React.useRef(index);
    indexRef.current = index;

    const onEndMove = React.useCallback(
        (el: HTMLDivElement) => {
            const clientWidth = el.clientWidth;

            el.style.transition = "transform 0.4s cubic-bezier(.17,1,.12,1)";

            const offset = dragOffset.current;
            const slop = clientWidth / 3;
            let next = index;
            if (offset > startOffset) {
                next = (offset + slop) / clientWidth;
            } else {
                next = (offset - slop) / clientWidth;
            }
            next = Math.max(0, Math.min(numChildren - 1, Math.round(next)));
            if (next !== index) {
                onIndexChange(next);
            } else {
                setTimeout(() => {
                    if (indexRef.current === index) {
                        setScroll(el, clientWidth * index, numChildren);
                    }
                }, 0);
            }
        },
        [index, startOffset, onIndexChange, numChildren, dragOffset]
    );

    const onClick = React.useCallback(
        (event: React.MouseEvent) => {
            if (captureClick) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [captureClick]
    );

    return (
        <MemoizedCarouselView
            {...p}
            onStartMove={onStartMove}
            onMove={onMove}
            onEndMove={onEndMove}
            onClick={onClick}
        />
    );
};

export default Carousel;
